import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/base/buttons/button";
import { Dialog, DialogTitle, ScrollableDialogContent } from "@/components/ui/dialog";
import { keywordFieldsFor, placeholderKeysFor, serializeKeywordList } from "./keyword-config";
import { ModalGroupSection } from "./ModalGroupSection";
import type { ModalRiskRowItem, SafetyPolicyOption } from "./ModalRiskRow";
import type { RiskCatalogEntry, RiskItem } from "./risk-groups";
import { buildRiskGroupsFromCatalog, severityToTier } from "./risk-groups";

/**
 * A risk as it is currently stored in whichever scope the modal edits. Both
 * `merchant_risks` and `workspace_risks` map onto this — the workspace passes its
 * `safety_policy_type` as `safetyPolicyId`, since it identifies the policy there.
 */
export type SavedRisk = {
  riskId: string;
  config: unknown;
  autoConfirm: boolean;
  safetyPolicyId: string | null;
  customCreatorRiskExplanation?: string | null;
};

export type RiskConfigPatch = {
  riskId: string;
  tier?: number;
  autoConfirm: boolean;
  safetyPolicyId: string | null;
  config?: Record<string, string[]>;
  customCreatorRiskExplanation: string | null;
};

/** A risk the modal opens on, asking the user to switch it on or off. */
export type RiskConfigFocus = { riskId: string; action: "on" | "off" };

/** What the user changed, split into the operations the save endpoints expect. */
export type RiskConfigDraft = {
  enabled: RiskConfigPatch[];
  updated: RiskConfigPatch[];
  disabled: string[];
  domainKnowledge?: Record<string, string>;
};

type RiskConfigModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  catalog: RiskCatalogEntry[] | undefined;
  savedRisks: SavedRisk[] | undefined;
  policies: SafetyPolicyOption[];
  savedDomainKnowledge: Record<string, string> | undefined;
  isSaving: boolean;
  onSave: (draft: RiskConfigDraft) => void;
  /** Merchant Details owns `own_brands` and shows it read-only here. Nothing owns
   *  it above a merchant, so a workspace template edits it inline. */
  managedPlaceholdersEditable?: boolean;
  focus?: RiskConfigFocus;
};

/**
 * The bulk risk editor, shared by the merchant Configure Risks modal and the
 * workspace Configure default risks modal. It owns the in-session draft and
 * turns it into a set of patches; persisting them is the caller's job, since
 * each scope has its own endpoint and payload shape.
 */
export const RiskConfigModal = ({
  open,
  onOpenChange,
  title,
  catalog,
  savedRisks,
  policies,
  savedDomainKnowledge,
  isSaving,
  onSave,
  managedPlaceholdersEditable,
  focus,
}: RiskConfigModalProps) => {
  const groups = useMemo(() => buildRiskGroupsFromCatalog(catalog ?? []), [catalog]);
  const allItems = useMemo(() => groups.flatMap((g) => g.items), [groups]);

  const [overrides, setOverrides] = useState<Record<string, Partial<ModalRiskRowItem>>>({});
  // Modal-level domain knowledge edits (placeholder keyword lists, keyed globally
  // since multiple risks can reference the same `{placeholder}`).
  const [domainKnowledge, setDomainKnowledge] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) {
      setOverrides({});
      setDomainKnowledge(savedDomainKnowledge ?? {});
    }
  }, [open, savedDomainKnowledge]);

  const riskById = new Map((savedRisks ?? []).map((r) => [r.riskId, r]));

  const getRiskState = (item: RiskItem): ModalRiskRowItem => {
    const keys = item.keys ?? [item.key];
    // Surface config from the first of a collapsed row's merged risks that is
    // already enabled.
    const enabledRisk = keys.map((k) => riskById.get(k)).find(Boolean);

    const savedEnabled = keys.some((k) => riskById.has(k));

    // Effective config: the saved override (which holds the admin-authored
    // `description` with `{placeholder}` tokens, and any keyword overrides)
    // coalesced over the catalog default.
    const savedConfig = (enabledRisk?.config as Record<string, unknown> | undefined) ?? {};
    const effectiveConfig = { ...item.config, ...savedConfig };
    const config: Record<string, string[]> = {};
    for (const field of keywordFieldsFor(item.modelType)) {
      const value = effectiveConfig[field];
      config[field] = Array.isArray(value) ? (value as string[]) : [];
    }

    const base: ModalRiskRowItem = {
      key: item.key,
      name: item.name,
      desc: item.desc,
      category: item.category,
      // Core Safety ("baseline") can never be disabled (UNB-2098): show it on and
      // locked even where the scope has stored no row for it (e.g. a workspace
      // default template), matching the merchant, where it is seeded on.
      enabled: item.category === "baseline" || savedEnabled,
      wasEnabled: savedEnabled,
      autoConfirm: enabledRisk?.autoConfirm ?? false,
      severity: item.defaultSeverity,
      safetyPolicyId: enabledRisk?.safetyPolicyId ?? null,
      modelType: item.modelType,
      config,
      placeholderKeys: placeholderKeysFor(item.modelType, effectiveConfig),
      customCreatorRiskExplanation: enabledRisk?.customCreatorRiskExplanation ?? null,
    };

    // The override never carries `wasEnabled`, so the saved state is preserved
    // even after the in-session toggle flips `enabled`.
    return { ...base, ...(overrides[item.key] ?? {}) };
  };

  const handleChange = (item: RiskItem, patch: Partial<ModalRiskRowItem>) => {
    setOverrides((prev) => ({ ...prev, [item.key]: { ...(prev[item.key] ?? {}), ...patch } }));
  };

  const handleDomainKnowledgeChange = (key: string, values: string[]) => {
    setDomainKnowledge((prev) => ({ ...prev, [key]: serializeKeywordList(values) }));
  };

  const handleSave = () => {
    const enabled: RiskConfigPatch[] = [];
    const updated: RiskConfigPatch[] = [];
    const disabled: string[] = [];

    for (const item of allItems) {
      if (!overrides[item.key]) continue;
      const state = getRiskState(item);
      // Only the keyword (stringArray) fields are editable config; omit entirely
      // for model types without any so we never send a partial config.
      const config = keywordFieldsFor(item.modelType).length > 0 ? state.config : undefined;
      // Preserve inheritance from the catalog default unless severity was
      // explicitly changed in this modal.
      const tier = state.severity === item.defaultSeverity ? undefined : severityToTier(state.severity);
      // A collapsed row applies its change to every merged risk id.
      for (const riskId of item.keys ?? [item.key]) {
        const wasEnabled = riskById.has(riskId);
        const patch: RiskConfigPatch = {
          riskId,
          ...(tier !== undefined && { tier }),
          autoConfirm: state.autoConfirm,
          safetyPolicyId: state.safetyPolicyId,
          config,
          customCreatorRiskExplanation: state.customCreatorRiskExplanation,
        };
        if (!wasEnabled && state.enabled) enabled.push(patch);
        else if (wasEnabled && !state.enabled) disabled.push(riskId);
        else if (wasEnabled && state.enabled) updated.push(patch);
      }
    }

    // Send only changed domain-knowledge entries (merged server-side).
    const domainKnowledgePatch: Record<string, string> = {};
    for (const [key, value] of Object.entries(domainKnowledge)) {
      if (value !== savedDomainKnowledge?.[key]) domainKnowledgePatch[key] = value;
    }

    onSave({
      enabled,
      updated,
      disabled,
      domainKnowledge: Object.keys(domainKnowledgePatch).length > 0 ? domainKnowledgePatch : undefined,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* The group list is the only scroll area, so the title and the save buttons stay
          in view. `overflow-clip`, not `hidden`: hidden still scrolls programmatically,
          and focusing a toggle low in the list drags the whole dialog with it. */}
      <ScrollableDialogContent
        className="flex max-h-[85vh] max-w-5xl flex-col gap-0 overflow-clip rounded-2xl border-none p-0 shadow-[0px_20px_24px_-4px_rgba(10,13,18,0.08),0px_8px_8px_-4px_rgba(10,13,18,0.03),0px_3px_3px_-1.5px_rgba(10,13,18,0.04)]"
        style={{ background: "#fffcf9" }}
      >
        <div className="flex items-center justify-between pt-6 px-6 pb-0">
          <DialogTitle className="text-lg font-semibold text-primary">{title}</DialogTitle>
        </div>

        {/* Block flow, not a flex column: the group cards clip their own overflow,
            so as flex children they would shrink to fit instead of scrolling. */}
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-6">
          {groups.map((group) => (
            <ModalGroupSection
              key={group.id}
              group={group}
              policies={policies}
              domainKnowledge={domainKnowledge}
              getRiskState={getRiskState}
              onRiskChange={handleChange}
              onDomainKnowledgeChange={handleDomainKnowledgeChange}
              managedPlaceholdersEditable={managedPlaceholdersEditable}
              focus={focus}
            />
          ))}
        </div>

        <div className="flex items-center justify-between px-6 py-4">
          <Button color="secondary" size="sm" onClick={() => onOpenChange(false)}>
            Back
          </Button>
          <Button color="primary" size="sm" isLoading={isSaving} onClick={handleSave}>
            Save
          </Button>
        </div>
      </ScrollableDialogContent>
    </Dialog>
  );
};
